import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Axios from 'axios';
import { Store } from '../Store';
import { api } from '../Api';
import { getError } from '../utils';
import '../assets/wallet.css';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v) =>
  num(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString([], {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

// How each kind of PSI movement reads on screen.
const TYPES = {
  credit: { label: 'Received', icon: 'fa-arrow-down', tone: 'wl-t1', sign: '+' },
  deduct: { label: 'Deducted', icon: 'fa-minus', tone: 'wl-tneg', sign: '−' },
  convert: { label: 'Converted', icon: 'fa-exchange', tone: 'wl-t3', sign: '−' },
};

const NO_TOTALS = { credited: 0, deducted: 0, converted: 0, current: 0 };

/** Profit Sharing Incentive (PSI): what the office sent, took back, and what was converted. */
export default function PsiHistoryScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const { search } = useLocation();
  const page = Math.max(parseInt(new URLSearchParams(search).get('page')) || 1, 1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState(NO_TOTALS);
  const [pages, setPages] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await Axios.get(api + `/api/users/psihistory?page=${page}`, {
        headers: { authorization: `Bearer ${userInfo.token}` },
      });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setTotals({ ...NO_TOTALS, ...(data?.totals || {}) });
      setPages(Math.max(num(data?.pages), 1));
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  }, [page, userInfo.token]);

  useEffect(() => {
    load();
  }, [load]);

  const tiles = [
    { key: 'cur', label: 'Current PSI', value: totals.current, icon: 'fa-pie-chart', tone: 'wl-t2' },
    { key: 'cr', label: 'Total received', value: totals.credited, icon: 'fa-arrow-down', tone: 'wl-t1' },
    { key: 'cv', label: 'Converted', value: totals.converted, icon: 'fa-exchange', tone: 'wl-t3' },
    { key: 'dd', label: 'Deducted', value: totals.deducted, icon: 'fa-minus', tone: 'wl-tneg' },
  ];

  return (
    <div className="wl-page">
      <Helmet>
        <title>PSI History</title>
      </Helmet>

      <div className="wl-wrap">
        <header className="wl-top">
          <button className="wl-back" onClick={() => navigate('/history')} aria-label="Back">
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i>
          </button>
          <div className="wl-top-title">
            <h1>PSI History</h1>
            <span>Profit Sharing Incentive</span>
          </div>
          <button
            className={`wl-refresh ${loading ? 'is-spinning' : ''}`}
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
          >
            <i className="fa fa-refresh" aria-hidden="true"></i>
          </button>
        </header>

        <div className="wl-tiles wl-tiles-4">
          {tiles.map((t) => (
            <div className="wl-tile" key={t.key}>
              <span className={`wl-tile-icon ${t.tone}`}>
                <i className={`fa ${t.icon}`} aria-hidden="true"></i>
              </span>
              <span>
                <small>{t.label}</small>
                <strong>{fmt(t.value)}</strong>
              </span>
            </div>
          ))}
        </div>

        {error && (
          <div className="wl-error">
            <i className="fa fa-exclamation-circle" aria-hidden="true"></i> {error}{' '}
            <button onClick={load}>Retry</button>
          </div>
        )}

        <section className="wl-card">
          <div className="wl-card-head">
            <h3>Activity</h3>
            {pages > 1 && (
              <span>
                Page {page} of {pages}
              </span>
            )}
          </div>

          {!loading && !error && entries.length === 0 && (
            <p className="wl-empty">
              No PSI yet. When the office sends you Profit Sharing Incentive it will appear here.
            </p>
          )}

          {entries.map((e) => {
            const t = TYPES[e.type] || TYPES.credit;
            return (
              <div className={`wl-entry ${loading ? 'is-loading' : ''}`} key={e._id}>
                <span className={`wl-ico ${t.tone}`}>
                  <i className={`fa ${t.icon}`} aria-hidden="true"></i>
                </span>
                <span className="wl-entry-main">
                  <b>{t.label}</b>
                  <em>{e.details}</em>
                  <em>{fmtDate(e.date)}</em>
                </span>
                <span className="wl-entry-amt">
                  <b className={e.type === 'credit' ? 'is-in' : 'is-out'}>
                    {t.sign}
                    {fmt(e.amount)}
                  </b>
                  {e.balanceAfter !== null && e.balanceAfter !== undefined && (
                    <em>Balance {fmt(e.balanceAfter)}</em>
                  )}
                </span>
              </div>
            );
          })}
        </section>

        {pages > 1 && (
          <nav className="wl-pager" aria-label="Pages">
            {page > 1 ? (
              <Link to={`/psihistory?page=${page - 1}`}>
                <i className="fa fa-angle-left" aria-hidden="true"></i> Newer
              </Link>
            ) : (
              <span />
            )}
            {page < pages ? (
              <Link to={`/psihistory?page=${page + 1}`}>
                Older <i className="fa fa-angle-right" aria-hidden="true"></i>
              </Link>
            ) : (
              <span />
            )}
          </nav>
        )}
      </div>
    </div>
  );
}
